import { MeshStandardMaterial, type ShaderMaterial, type BufferGeometry, type Color, type Material, type Mesh, type Object3D } from "three";
import type { AtmosphereUniforms } from "../atmosphere.ts";
import { gameColor, unwatchGameColor, watchGameColor, type GameColorWatcher } from "../style/roles.ts";
import { clampStyle, type CharacterSurfaceStyle } from "./config.ts";
import { characterBaseMaterial, createEnergyMaterial, energyUniforms, energyVariant, writeEnergyStyle, type EnergyLook, type EnergyUniforms } from "./material.ts";
import { isPrepared, PREPARED, type PreparedGeometries } from "./prepare-geometry.ts";

/**
 * A superfície num ator (F118): troca, sob a raiz, a geometria de cada malha pela preparada e o
 * material pelo PBR corrigido, e devolve o controle por uniforms. Cada ator tem os próprios uniforms
 * (dois da mesma classe não pulsam juntos; cores diferentes ao mesmo tempo); o programa é um só.
 */

export interface CharacterSurfaceContext {
  atmosphere: AtmosphereUniforms;
  /** Rim de luz, rim noturno e tingimento da máscara. */
  look?: EnergyLook;
  /** Quais malhas recebem (padrão: todas, menos as marcadas com `userData.characterSurface === false`). */
  include?: (mesh: Mesh) => boolean;
  /** O cache das geometrias preparadas (padrão, o do jogo). */
  geometries?: PreparedGeometries;
}

export interface CharacterSurfaceHandle {
  /** O estilo em uso (a cor é a pedida; o shader recebe a do modo daltônico, se ligado). */
  readonly style: Readonly<CharacterSurfaceStyle>;
  /** Os materiais (inteiros) do ator: para a pré-compilação e os testes. */
  readonly materials: readonly MeshStandardMaterial[];
  /** As malhas que receberam a superfície. */
  readonly meshes: readonly Mesh[];
  setAccentColor(hex: string): void;
  setEnabled(enabled: boolean): void;
  /** Costura acesa (o envelope do golpe) e Fresnel (o rim do papel, a ameaça). */
  setSeam(value: number): void;
  setFresnel(value: number): void;
  /** Vários campos de uma vez (todos são uniforms). */
  set(style: Partial<CharacterSurfaceStyle>): void;
  /** Rim de luz, rim noturno e tingimento da máscara. */
  setLook(look: EnergyLook): void;
  /** Dissolve (0 inteiro, 1 sumido); sem `edge`, sem a borda acesa. Troca para a variante e volta. */
  setDissolve(amount: number, edge?: boolean): void;
  /** Devolve as geometrias e os materiais originais e descarta só o que é da superfície. */
  dispose(): void;
}

/**
 * Material PBR de partida pelo original: dele, só a cor (a do material do three ou a sólida da
 * superfície, `uColor`) e a cor por vértice; rugosidade e metal são sempre os dos atores. Os GLBs do
 * jogo não têm material (ADR 0009): o que o `GLTFLoader` põe neles é o padrão do glTF, com metal 1, que
 * apagaria toda a luz difusa (o ator ficava preto fora do rim).
 */
function baseFor(material: Material, geometry: BufferGeometry): MeshStandardMaterial {
  const vertexColors = geometry.getAttribute("color") !== undefined && material.vertexColors;
  const color = material instanceof MeshStandardMaterial ? material.color : ((material as ShaderMaterial).uniforms?.["uColor"]?.value as Color | undefined);
  return characterBaseMaterial({ vertexColors, ...(color && { color: `#${color.getHexString()}` }) });
}

/**
 * Por padrão, toda malha menos as marcadas (`userData.characterSurface === false`) e as que brilham
 * sozinhas (`uGlow` da superfície: olhos, fogo, núcleos; são fonte de luz, não superfície iluminada).
 */
const defaultInclude = (mesh: Mesh) => mesh.userData["characterSurface"] !== false && !((mesh.material as ShaderMaterial).uniforms?.["uGlow"]?.value > 0.5);

/**
 * Prende quem segue o modo daltônico aos materiais do ator: o registro guarda só uma referência fraca,
 * e um controle descartado pelo chamador com as malhas ainda na cena pararia de seguir a troca.
 */
const retained = new WeakMap<Material, GameColorWatcher>();

/** O material segura quem o faz seguir o modo daltônico (testes). */
export function followsColorblind(material: Material): boolean {
  return retained.has(material);
}

export function applyCharacterSurface(root: Object3D, style: Partial<CharacterSurfaceStyle>, ctx: CharacterSurfaceContext): CharacterSurfaceHandle {
  const current = clampStyle(style);
  const geometries = ctx.geometries ?? PREPARED;
  const include = ctx.include ?? defaultInclude;
  const uniforms: EnergyUniforms = energyUniforms(current, ctx.look);
  const write = () => writeEnergyStyle(uniforms, current, gameColor(current.accentColor));
  write();

  const meshes: Mesh[] = [];
  root.traverse((o) => {
    const mesh = o as Mesh;
    if (mesh.isMesh && !Array.isArray(mesh.material) && include(mesh)) meshes.push(mesh);
  });

  // Geometrias primeiro, todas ou nenhuma (uma malha fora de triângulos lança erro sem deixar o ator
  // pela metade); a já preparada (`prepareTree`) entra como está e não é devolvida ao cache.
  const originals = meshes.map((mesh) => ({ mesh, geometry: mesh.geometry, material: mesh.material as Material, acquired: false }));
  const prepared: BufferGeometry[] = [];
  try {
    for (const o of originals) {
      if (isPrepared(o.geometry)) {
        prepared.push(o.geometry);
        continue;
      }
      prepared.push(geometries.acquire(o.geometry));
      o.acquired = true;
    }
  } catch (error) {
    originals.forEach((o, i) => o.acquired && geometries.release(prepared[i]!));
    throw error;
  }

  // Um material corrigido por material original e pela cor por vértice da malha (o ator inteiro divide
  // os uniforms): o mesmo original numa malha com `color` e noutra sem vira dois materiais.
  const patched = new Map<string, MeshStandardMaterial>();
  const own: MeshStandardMaterial[] = [];
  originals.forEach(({ mesh, geometry, material }, i) => {
    mesh.geometry = prepared[i]!;
    const key = `${material.uuid}|${geometry.getAttribute("color") !== undefined}`;
    let out = patched.get(key);
    if (!out) {
      const base = baseFor(material, geometry);
      out = createEnergyMaterial(base, ctx.atmosphere, uniforms);
      base.dispose();
      patched.set(key, out);
      own.push(out);
    }
    mesh.material = out;
  });

  let dissolves: Map<MeshStandardMaterial, MeshStandardMaterial> | undefined;
  const watcher: GameColorWatcher = { applyGameColor: write };
  watchGameColor(watcher);
  for (const m of own) retained.set(m, watcher);
  let disposed = false;

  const handle: CharacterSurfaceHandle = {
    get style() {
      return current;
    },
    materials: own,
    meshes,
    setAccentColor(hex) {
      current.accentColor = hex;
      write();
    },
    setEnabled(enabled) {
      current.enabled = enabled;
      uniforms.uEnergyEnabled.value = enabled ? 1 : 0;
    },
    setSeam(value) {
      // Todo quadro (o envelope do golpe): direto, sem refazer o estilo inteiro.
      current.seamIntensity = value;
      uniforms.uEnergySeam.value = value;
    },
    setFresnel(value) {
      current.fresnel = value;
      uniforms.uEnergyFresnel.value = value;
    },
    set(partial) {
      Object.assign(current, clampStyle({ ...current, ...partial }));
      write();
    },
    setLook(look) {
      if (look.rim !== undefined) uniforms.uRim.value = look.rim;
      if (look.nightRim !== undefined) uniforms.uRimNightOn.value = look.nightRim ? 1 : 0;
      if (look.tintRef !== undefined) uniforms.uEnergyTintRef.value = look.tintRef;
    },
    setDissolve(amount, edge = true) {
      if (amount <= 0) {
        for (const mesh of meshes) {
          const m = mesh.material as MeshStandardMaterial;
          for (const [base, variant] of dissolves ?? []) if (m === variant) mesh.material = base;
        }
        uniforms.uDissolve.value = 0;
        return;
      }
      // Uma variante por material do ator, criada só quando ele some ou volta (o programa é um só).
      if (!dissolves) {
        dissolves = new Map(own.map((m) => [m, energyVariant(m, { defines: { USE_DISSOLVE: "" } })]));
        for (const variant of dissolves.values()) retained.set(variant, watcher);
      }
      uniforms.uDissolve.value = edge ? amount : -amount;
      for (const mesh of meshes) {
        const variant = dissolves.get(mesh.material as MeshStandardMaterial);
        if (variant) mesh.material = variant;
      }
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      unwatchGameColor(watcher);
      originals.forEach(({ mesh, geometry, material, acquired }, i) => {
        if (acquired) geometries.release(prepared[i]!);
        mesh.geometry = geometry;
        mesh.material = material;
      });
      for (const m of own) m.dispose();
      for (const m of dissolves?.values() ?? []) m.dispose();
    },
  };
  return handle;
}

/**
 * Materiais divididos (os bonecos procedurais das criaturas no lote, F36): um por cor, acento e visual,
 * por atmosfera, como o `toonMaterial`. Num lote, as instâncias dividem o material, então não há
 * uniforms por ator; a cor segue o modo daltônico pelo mesmo registro. Nada de escrever por desenho
 * (`onBeforeRender`): o `uniformsNeedUpdate` só vale para `ShaderMaterial`, e o three não reenvia os
 * uniforms entre desenhos seguidos do mesmo material.
 */
const shared = new WeakMap<AtmosphereUniforms, Map<string, { material: MeshStandardMaterial; watcher: GameColorWatcher }>>();

export function sharedCharacterMaterial(atmosphere: AtmosphereUniforms, o: { color: string; style?: Partial<CharacterSurfaceStyle>; look?: EnergyLook }): MeshStandardMaterial {
  const style = clampStyle(o.style);
  // Só o que o material usa, em ordem fixa.
  const look = { rim: o.look?.rim ?? 0.8, nightRim: o.look?.nightRim === true, tintRef: o.look?.tintRef ?? 0 };
  const key = JSON.stringify([o.color, style.enabled, style.accentColor, style.seamIntensity, style.fresnel, style.glass, look.rim, look.nightRim, look.tintRef]);
  let cache = shared.get(atmosphere);
  if (!cache) shared.set(atmosphere, (cache = new Map()));
  let entry = cache.get(key);
  if (!entry) {
    const uniforms = energyUniforms(style, look);
    const base = characterBaseMaterial({ color: o.color });
    const material = createEnergyMaterial(base, atmosphere, uniforms);
    base.dispose();
    const watcher: GameColorWatcher = { applyGameColor: () => writeEnergyStyle(uniforms, style, gameColor(style.accentColor)) };
    watcher.applyGameColor();
    watchGameColor(watcher);
    cache.set(key, (entry = { material, watcher }));
  }
  return entry.material;
}

/** Troca, sob `root`, a geometria das malhas incluídas pela preparada (de vez: as primitivas dos bonecos vivem a página toda). */
export function prepareTree(root: Object3D, o: { include?: (mesh: Mesh) => boolean; geometries?: PreparedGeometries } = {}): void {
  const geometries = o.geometries ?? PREPARED;
  const include = o.include ?? defaultInclude;
  root.traverse((obj) => {
    const mesh = obj as Mesh;
    if (!mesh.isMesh || !include(mesh) || isPrepared(mesh.geometry)) return;
    mesh.geometry = geometries.acquire(mesh.geometry);
  });
}
